import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface ProductModel {
  id: number;
  nome?: string;
}

export interface ProductModelFormData {
  nome?: string;
}

export function useProductModels() {
  const queryClient = useQueryClient();

  const { data: models = [], isLoading } = useQuery({
    queryKey: ["product-models"],
    queryFn: async () => {
      const { data } = await apiClient.get<ProductModel[]>("/api/modelos");
      return data;
    },
  });

  const createModel = useMutation({
    mutationFn: async (formData: ProductModelFormData) => {
      const { data } = await apiClient.post<ProductModel>("/api/modelos", formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-models"] });
      toast.success("Modelo criado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao criar modelo: " + error.message);
    },
  });

  const updateModel = useMutation({
    mutationFn: async ({ id, ...formData }: ProductModelFormData & { id: number }) => {
      const { data } = await apiClient.put<ProductModel>(`/api/modelos/${id}`, formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-models"] });
      toast.success("Modelo atualizado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao atualizar modelo: " + error.message);
    },
  });

  const deleteModel = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/modelos/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-models"] });
      toast.success("Modelo excluído com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao excluir modelo: " + error.message);
    },
  });

  return {
    models,
    isLoading,
    createModel,
    updateModel,
    deleteModel,
  };
}
